import { MIN_ARRAY_LENGTH_FOR_INNER_CALCULATION } from "../../constants";
import { IStationAreasInfo, IStationInfo } from "../../dto";
import { unique } from "../array.helpers";
import { isStationRouteWaypointStation } from "../station-route";

const EMPTY_STATION_AREAS_INFO = {
  areaIds: [],
  innerAreaIds: [],
  stationIds: [],
  pickupStationsIds: [],
  dropoffStationsIds: [],
} satisfies IStationAreasInfo;

export const getInnerAreas = (
  stations: Pick<IStationInfo, "areaId">[],
): number[] => {
  if (
    !stations?.length ||
    stations.length < MIN_ARRAY_LENGTH_FOR_INNER_CALCULATION
  ) {
    return [];
  }

  const firstArea = stations[0].areaId;
  const lastArea = stations[stations.length - 1].areaId;

  const innerStations = stations.slice(1, -1);

  const innerAreaIds = innerStations
    .map(s => Number(s.areaId))
    .filter(a => !!a);

  let startIndex = 0;

  while (
    startIndex < innerAreaIds.length &&
    innerAreaIds[startIndex] === firstArea
  ) {
    startIndex++;
  }

  let endIndex = innerAreaIds.length;

  while (endIndex > startIndex && innerAreaIds[endIndex - 1] === lastArea) {
    endIndex--;
  }

  const trimmed = innerAreaIds.slice(startIndex, endIndex);

  return Array.from(new Set(trimmed));
};

export const getStationAreasInfo = (
  stations: IStationInfo[],
): IStationAreasInfo => {
  if (!stations?.length) {
    return EMPTY_STATION_AREAS_INFO;
  }

  const realStations = stations.filter(
    station => !isStationRouteWaypointStation(station),
  );
  const stationIds = realStations
    .map(station => station.id)
    .filter((id): id is number => Number(id) > 0);
  const areaIds = unique(
    realStations
      .map(station => station.areaId)
      .filter(Number)
      .map(Number),
  );
  const innerAreaIds = getInnerAreas(realStations);
  const pickupStationsIds = realStations
    .filter(station => station.isBoarding)
    .map(station => station.id!)
    .filter(Number);
  const dropoffStationsIds = realStations
    .filter(station => station.isDropoff)
    .map(station => station.id!)
    .filter(Number);

  return {
    areaIds,
    innerAreaIds,
    stationIds,
    pickupStationsIds,
    dropoffStationsIds,
  } satisfies IStationAreasInfo;
};
